import React, { useState, useRef } from 'react';
import { useLMS } from '../../context/LMSContext';
import { authHelpers, supabase } from '../../lib/supabase';

const StudentProfile = () => {
  const {
    currentUser,
    currentCohort,
    updateOwnProfile
  } = useLMS();

  // Form state - initialize from current user
  const [name, setName] = useState(currentUser?.name || '');
  const [nclexDate, setNclexDate] = useState(currentUser?.nclex_test_date || '');
  const [tiktok, setTiktok] = useState(currentUser?.tiktok_handle || '');
  const [instagram, setInstagram] = useState(currentUser?.instagram_handle || '');
  const [facebook, setFacebook] = useState(currentUser?.facebook_handle || '');

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const fileInputRef = useRef(null);
  const cohort = currentCohort;

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  // Save profile changes
  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      showMessage('error', 'Name cannot be empty.');
      return;
    }

    setSaving(true);
    const result = await updateOwnProfile({
      name: name.trim(),
      nclex_test_date: nclexDate || null,
      tiktok_handle: tiktok.trim(),
      instagram_handle: instagram.trim(),
      facebook_handle: facebook.trim()
    });
    setSaving(false);

    if (result.error) {
      showMessage('error', 'Failed to save: ' + result.error);
    } else {
      showMessage('success', 'Profile updated successfully!');
    }
  };

  // Avatar upload
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      showMessage('error', 'Please select an image file.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showMessage('error', 'Image must be under 2MB.');
      return;
    }

    setUploadingAvatar(true);
    try {
      const ext = file.name.split('.').pop();
      const filePath = `avatars/${currentUser.id}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Add cache-buster to force refresh
      const avatarUrl = `${publicUrl}?t=${Date.now()}`;
      const result = await updateOwnProfile({ avatar_url: avatarUrl });

      if (result.error) throw new Error(result.error);
      showMessage('success', 'Avatar updated!');
    } catch (err) {
      console.error('Avatar upload error:', err);
      showMessage('error', 'Failed to upload avatar. Make sure the avatars storage bucket exists.');
    }
    setUploadingAvatar(false);
    // Clear file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Password reset
  const handlePasswordReset = async () => {
    setResetEmailSent(false);
    try {
      const { error } = await authHelpers.resetPassword(currentUser.email);
      if (error) throw error;
      setResetEmailSent(true);
      showMessage('success', 'Password reset email sent! Check your inbox.');
    } catch (err) {
      showMessage('error', 'Failed to send reset email: ' + err.message);
    }
  };

  // NCLEX countdown
  const getDaysUntilNclex = () => {
    if (!nclexDate) return null;
    const [year, month, day] = nclexDate.split('-').map(Number);
    const testDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((testDate - today) / (1000 * 60 * 60 * 24));
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const daysUntil = getDaysUntilNclex();
  const initials = (currentUser?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase();

  return (
    <div className="profile-view">
      <div className="profile-header">
        <h1>My Profile</h1>
      </div>

      {/* Status message */}
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="profile-card">
        {/* Avatar Section */}
        <div className="avatar-section">
          <div className="avatar-container">
            {currentUser?.avatar_url ? (
              <img src={currentUser.avatar_url} alt={currentUser.name} className="avatar-image" />
            ) : (
              <div className="avatar-placeholder">{initials}</div>
            )}
            <button
              type="button"
              className="avatar-upload-btn"
              onClick={handleAvatarClick}
              disabled={uploadingAvatar}
              title="Change avatar"
            >
              {uploadingAvatar ? '...' : '📷'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              style={{ display: 'none' }}
            />
          </div>
          <div className="avatar-info">
            <h2>{currentUser?.name}</h2>
            <p>{currentUser?.email}</p>
            {cohort && <p>{cohort.name}</p>}
          </div>
        </div>

        {/* Profile Form */}
        <form className="profile-form" onSubmit={handleSave}>
          {/* Personal Info */}
          <div className="form-section">
            <h3>Personal Information</h3>
            <p className="section-hint">Update your name and contact details.</p>

            <div className="form-row">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={currentUser?.email || ''}
                  disabled
                />
              </div>
            </div>
          </div>

          {/* NCLEX Test Date */}
          <div className="form-section">
            <h3>NCLEX Test Date</h3>
            <p className="section-hint">Enter your scheduled NCLEX exam date so your instructors can track your timeline.</p>

            <div className="form-group">
              <label>Exam Date</label>
              <input
                type="date"
                value={nclexDate}
                onChange={(e) => setNclexDate(e.target.value)}
              />
            </div>

            {nclexDate && daysUntil !== null && (
              <div style={{
                padding: '14px 16px',
                borderRadius: '10px',
                backgroundColor: daysUntil < 0 ? 'var(--gray-100, #f3f4f6)' : daysUntil <= 7 ? 'var(--error-light, #fef2f2)' : daysUntil <= 30 ? '#fffbeb' : 'var(--success-light, #ecfdf5)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginTop: '8px'
              }}>
                <span style={{ fontSize: '24px' }}>
                  {daysUntil < 0 ? '✅' : daysUntil <= 7 ? '🔥' : daysUntil <= 30 ? '⏰' : '📋'}
                </span>
                <div>
                  <strong>
                    {daysUntil < 0
                      ? 'Your NCLEX date has passed'
                      : daysUntil === 0
                        ? 'Your NCLEX is TODAY!'
                        : `${daysUntil} day${daysUntil !== 1 ? 's' : ''} until your NCLEX`
                    }
                  </strong>
                  <div style={{ fontSize: '13px', opacity: 0.7, marginTop: '2px' }}>
                    {formatDisplayDate(nclexDate)}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Social Media */}
          <div className="form-section">
            <h3>Social Media</h3>
            <p className="section-hint">Share your social media handles with your cohort.</p>

            <div className="form-group">
              <label><span className="social-icon">📱</span> TikTok</label>
              <div className="social-input">
                <span className="input-prefix">@</span>
                <input
                  type="text"
                  value={tiktok}
                  onChange={(e) => setTiktok(e.target.value)}
                  placeholder="username"
                />
              </div>
            </div>

            <div className="form-group">
              <label><span className="social-icon">📸</span> Instagram</label>
              <div className="social-input">
                <span className="input-prefix">@</span>
                <input
                  type="text"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  placeholder="username"
                />
              </div>
            </div>

            <div className="form-group">
              <label><span className="social-icon">👥</span> Facebook</label>
              <div className="social-input">
                <span className="input-prefix">@</span>
                <input
                  type="text"
                  value={facebook}
                  onChange={(e) => setFacebook(e.target.value)}
                  placeholder="username"
                />
              </div>
            </div>
          </div>

          {/* Password */}
          <div className="form-section">
            <h3>Password</h3>
            <p className="section-hint">Request a password reset email to change your password.</p>

            <div className="password-field">
              <button
                type="button"
                className="btn-secondary"
                onClick={handlePasswordReset}
              >
                📧 Send Password Reset Email
              </button>
            </div>
            {resetEmailSent && (
              <p style={{ marginTop: '8px', fontSize: '13px', color: 'var(--success, #10b981)' }}>
                Check your email for the reset link.
              </p>
            )}
          </div>

          {/* Save Button */}
          <div className="profile-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentProfile;
